/**
 * Runtime measurement of the Android system navigation bar inset (Despia).
 *
 * PROBLEM: `index.css` used to reserve a flat `--android-nav-inset: 48px` for
 * every Android device. That clears a 3-button navigation bar, but phones with
 * gesture navigation only need ~16px (or nothing at all), so the floating dock
 * ended up absurdly high on them.
 *
 * THERE IS NO WAY TO KNOW THIS UP FRONT. The only semantically correct source
 * is the inset the system itself reports — the OS does distinguish gestures
 * (~16dp) from a 3-button bar (~48dp). Measured on two real devices (Tactic,
 * 2026-07-29, same Despia wrapper): `env(safe-area-inset-bottom)` is **0** on
 * Android, but Despia's injected `--safe-area-bottom` reports the real value
 * (47.29 with 3 buttons, 16 with gestures). If neither reports anything, this
 * module writes NOTHING and the CSS fallback stays in charge — zero regression
 * by design: the worst case is "exactly like before".
 *
 * ARCHITECTURE: the DOM probe and the decision are deliberately separate. The
 * probe is the only correct way to resolve these values — reading the custom
 * property with `getComputedStyle()` returns the unresolved token stream (the
 * literal string `env(safe-area-inset-bottom)`), whereas a `height` forces the
 * engine to compute the used value in px.
 *
 * NO-OP outside `html.despia-android`: iOS native, mobile web and desktop never
 * get here — no probes, no timers, no listeners.
 */

/** Raw signals measured on the device. All values in CSS px. */
export interface InsetSignals {
  /** Height of a probe with `height: env(safe-area-inset-bottom, 0px)`. */
  envBottomPx: number;
  /** Height of a probe with `height: var(--safe-area-bottom, 0px)` (Despia). */
  varBottomPx: number;
  screenHeight: number;
  innerHeight: number;
  /** `null` when the browser does not expose `visualViewport`. */
  visualViewportHeight: number | null;
  isAndroidNative: boolean;
}

export type InsetSource =
  /** Not Android native: nothing is touched. */
  | "not-android"
  /** `env()` / `--safe-area-bottom` report a real value: source of truth. */
  | "platform"
  /** The WebView already excludes the system bars from the viewport. */
  | "viewport-inset"
  /** On-screen keyboard is up: the measurement is not trustworthy. */
  | "keyboard-open"
  /** No usable signal: nothing is written, the CSS fallback wins. */
  | "unknown";

export interface InsetDecision {
  /** `null` means "do not write". */
  px: number | null;
  source: InsetSource;
}

/** A 3-button bar is ~48dp; leave room for tablets and odd cutouts. */
const MAX_INSET_PX = 72;
/** Below this it is subpixel noise, not a system bar. */
const MIN_TRUSTED_PX = 4;
/** Same threshold used elsewhere to detect the virtual keyboard. */
const KEYBOARD_DELTA_PX = 150;
/**
 * `screen.height - innerHeight` above which we assume the WebView is NOT
 * edge-to-edge: status bar (~24-32) + navigation bar (~24-48) already outside
 * the viewport. Below that nothing can be concluded.
 */
const VIEWPORT_EXCLUDES_BARS_PX = 60;

const clamp = (n: number): number =>
  Math.min(MAX_INSET_PX, Math.max(0, Math.round(n)));

/**
 * Decide the inset from the measured signals. PURE: does not touch the DOM.
 *
 * Precedence (most to least trustworthy):
 *  1. Not Android native → no-op.
 *  2. Keyboard is up → skip (the dock would jump and come back).
 *  3. `env()` / `--safe-area-bottom` → the system is telling us the truth.
 *  4. The viewport already excludes the bars → inset 0 (today we add 48px too many).
 *  5. No signal → `unknown`: write nothing, the CSS fallback stays.
 */
export function decideAndroidNavInset(s: InsetSignals): InsetDecision {
  if (!s.isAndroidNative) return { px: null, source: "not-android" };

  if (
    s.visualViewportHeight !== null &&
    s.innerHeight - s.visualViewportHeight > KEYBOARD_DELTA_PX
  ) {
    return { px: null, source: "keyboard-open" };
  }

  const reported = Math.max(s.envBottomPx, s.varBottomPx);
  if (reported >= MIN_TRUSTED_PX) {
    return { px: clamp(reported), source: "platform" };
  }

  const gap = s.screenHeight - s.innerHeight;
  if (Number.isFinite(gap) && gap >= VIEWPORT_EXCLUDES_BARS_PX) {
    return { px: 0, source: "viewport-inset" };
  }

  return { px: null, source: "unknown" };
}

// ---------------------------------------------------------------------------
// DOM probe
// ---------------------------------------------------------------------------

type ProbeKey = "envBottom" | "varBottom";

let probeRoot: HTMLDivElement | null = null;
let probes: Record<ProbeKey, HTMLDivElement> | null = null;

function isAndroidNativeDocument(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("despia-android")
  );
}

/** Hidden probes whose HEIGHT is the expression we need resolved to px. */
function ensureProbes(): void {
  if (probeRoot || typeof document === "undefined" || !document.body) return;

  probeRoot = document.createElement("div");
  probeRoot.setAttribute("aria-hidden", "true");
  probeRoot.style.cssText =
    "position:fixed;left:0;bottom:0;width:0;height:0;overflow:hidden;" +
    "visibility:hidden;pointer-events:none;z-index:-1;";

  const make = (height: string): HTMLDivElement => {
    const el = document.createElement("div");
    el.style.cssText = `width:1px;height:${height};`;
    probeRoot!.appendChild(el);
    return el;
  };

  probes = {
    envBottom: make("env(safe-area-inset-bottom, 0px)"),
    varBottom: make("var(--safe-area-bottom, 0px)"),
  };

  document.body.appendChild(probeRoot);
}

/** Read every signal from the device. `null` when there is no DOM. */
export function readInsetSignals(): InsetSignals | null {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return null;
  }
  ensureProbes();

  const px = (el: HTMLDivElement | undefined): number =>
    el ? el.getBoundingClientRect().height : 0;

  return {
    envBottomPx: px(probes?.envBottom),
    varBottomPx: px(probes?.varBottom),
    screenHeight: window.screen?.height ?? 0,
    innerHeight: window.innerHeight ?? 0,
    visualViewportHeight: window.visualViewport?.height ?? null,
    isAndroidNative: isAndroidNativeDocument(),
  };
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

let lastWritten: number | null = null;

/**
 * Measure and write `--android-nav-inset` inline on `<html>`. The inline style
 * beats the `html.despia-android { --android-nav-inset: 48px }` rule in
 * `index.css`, which stays purely as a safety net for the `unknown` case.
 *
 * Idempotent: nothing is written when the value has not changed.
 */
export function applyAndroidNavInset(): InsetDecision {
  if (!isAndroidNativeDocument()) return { px: null, source: "not-android" };

  const signals = readInsetSignals();
  if (!signals) return { px: null, source: "not-android" };

  const decision = decideAndroidNavInset(signals);
  const html = document.documentElement;

  if (decision.px === null) {
    // `unknown` = nothing measurable (the WebView consumes the WindowInsets
    // natively). The 48px CSS fallback is in charge, so there IS reserved room
    // under the dock and the scrim is needed too — without this the very case
    // the fallback exists for would keep showing sharp content behind the
    // system buttons. `keyboard-open` leaves whatever class was already there.
    if (decision.source === "unknown") html.classList.add("has-nav-inset");
    return decision;
  }

  if (decision.px === lastWritten) return decision;
  lastWritten = decision.px;
  html.style.setProperty("--android-nav-inset", `${decision.px}px`);
  html.classList.toggle("has-nav-inset", decision.px > 0);
  return decision;
}

/**
 * Install the watcher. Measures immediately and retries a few times, because
 * Despia may inject `--safe-area-*` asynchronously after boot. Returns a
 * `dispose()`.
 */
export function installAndroidInsetWatcher(): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  // Early exit outside Android native: no probes, no timers, no listeners.
  if (!isAndroidNativeDocument()) return () => {};

  let disposed = false;
  let frame = 0;

  const schedule = (): void => {
    if (disposed || frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      if (!disposed) applyAndroidNavInset();
    });
  };

  applyAndroidNavInset();

  const timers = [100, 300, 1000, 3000].map((ms) =>
    window.setTimeout(schedule, ms),
  );

  window.addEventListener("resize", schedule);
  window.addEventListener("orientationchange", schedule);
  window.addEventListener("pageshow", schedule);
  document.addEventListener("visibilitychange", schedule);
  window.visualViewport?.addEventListener("resize", schedule);

  return () => {
    disposed = true;
    if (frame) window.cancelAnimationFrame(frame);
    timers.forEach((id) => window.clearTimeout(id));
    window.removeEventListener("resize", schedule);
    window.removeEventListener("orientationchange", schedule);
    window.removeEventListener("pageshow", schedule);
    document.removeEventListener("visibilitychange", schedule);
    window.visualViewport?.removeEventListener("resize", schedule);
  };
}
